// @flow strict

import React, { Fragment } from 'react';

import { graphql } from 'gatsby';
import { Box, Grid } from '@material-ui/core';

import { AboutUs } from '~components/about-us/about-us';
import { Layout } from '~components/layout/layout';
import { AuthorListItem } from '~components/author/author-list-item';
import { FrontPost } from '~components/blog-elements/front-post';
import { NewsletterSignup } from '~components/newsletter-signup/newsletter-signup';
import { PostPreview } from '~components/blog-elements/post-preview';

import { useLanguageContext } from '~contexts/language-context';

import type { Author } from '~types/author';
import type { Alternates, PostNode } from '~types/post';

import { withApp } from '../app';

import '~fragments/author';

import * as styles from './author.module.less';

/**
 * Type of component properties
 */
type AuthorPageProps = {
  data: {
    authorJson: Author,
    allMarkdownRemark: { nodes: Array<PostNode> },
  },

  pageContext: {
    alternates: Alternates,
  },
};

/**
 * Template for author pages.
 *
 * @param {AuthorPageProps} props - component properties
 *
 * @returns {node}
 */
const AuthorPageTemplate = ({ data, pageContext }: AuthorPageProps) => {
  const author = data.authorJson;
  const posts = data.allMarkdownRemark.nodes;

  const { language, loc } = useLanguageContext();

  // render post previews
  const postsContent =
    posts.length > 0 ? (
      <Fragment>
        <section className="container">
          <FrontPost className={styles.post} post={posts[0]} />
        </section>

        <section className="container">
          <Grid container spacing={6}>
            {posts.slice(1).map((post, idx) => {
              return (
                <Grid item key={post.id} xs={12} sm={6} lg={4}>
                  <PostPreview
                    className={styles.post}
                    post={post}
                    hideImage={idx % 2 === 0}
                  />
                </Grid>
              );
            })}
          </Grid>
        </section>
      </Fragment>
    ) : (
      <div className="container">No Posts</div>
    );

  const seoFields = {
    title: author.name,
    description: author.descriptions[language],
    alternates: pageContext.alternates,
    meta: [
      {
        name: 'robots',
        content: 'noindex, nofollow',
      },
    ],
  };

  return (
    <Layout seoFields={seoFields} alternates={pageContext.alternates}>
      {/* Author information, hidden on mobile */}
      <Box display={{ xs: 'none', sm: 'block' }}>
        <div className="container">
          <div className={styles.author}>
            <AuthorListItem author={author} />
          </div>
        </div>
      </Box>

      {/* headline with author name */}
      <header className="container">
        <h3 className={styles.headline}>
          {loc('_author_latest_posts')} {author.name}
        </h3>
      </header>

      {postsContent}

      <div className="container">
        <AboutUs />
      </div>

      <div className="container">
        <NewsletterSignup />
      </div>
    </Layout>
  );
};

export default withApp<AuthorPageProps>(AuthorPageTemplate);

// noinspection JSUnusedGlobalSymbols
export const pageQuery = graphql`
  query AuthorById($id: String!, $language: String!, $dateFormat: String!) {
    authorJson(id: { eq: $id }) {
      ...Author
    }

    allMarkdownRemark(
      sort: { fields: [frontmatter___date], order: DESC }
      filter: {
        fields: {
          authors: { elemMatch: { id: { in: [$id] } } }
          draft: { eq: false }
        }
        frontmatter: { language: { eq: $language } }
      }
    ) {
      nodes {
        ...FrontPost
        ...PostPreview
      }
    }
  }
`;
